import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailMode, setEmailMode] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup(email, password, name);
      toast.success("Account created — let's find your dish");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="signup-page">
      <div className="overline mb-3">Build your Taste DNA</div>
      <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight mb-8">Create your YUMO</h1>
      <Card className="p-7 rounded-2xl border-border shadow-none">
        <GoogleAuthButton label="Continue with Google" testId="signup-google-btn" />
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-border" />
          <span className="overline">OR</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        {!emailMode ? (
          <Button
            type="button"
            variant="ghost"
            className="w-full rounded-full h-11"
            onClick={() => setEmailMode(true)}
            data-testid="signup-show-email"
          >
            Continue with Email
          </Button>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="signup-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="signup-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="signup-password-input" />
              <p className="text-[11px] text-muted-foreground">Min 8 characters · letters + numbers</p>
            </div>
            {error && <div className="text-sm text-destructive" data-testid="signup-error">{error}</div>}
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="signup-submit-button">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
        )}
      </Card>
      <p className="text-sm text-muted-foreground mt-6">
        Already on YUMO?{" "}
        <Link to="/login" className="text-foreground underline underline-offset-4" data-testid="goto-login-link">Log in</Link>
      </p>
    </div>
  );
}
